import React from 'react';

interface ExportButtonsProps {
  onExportCSV: () => void;
  onExportParquet: () => void;
  disabled?: boolean;
  hasResults: boolean;
}

const ExportButtons: React.FC<ExportButtonsProps> = ({ 
  onExportCSV, 
  onExportParquet, 
  disabled = false, 
  hasResults 
}) => {
  const isButtonDisabled = disabled || !hasResults;

  return (
    <div className="w-full max-w-5xl mx-auto space-y-8">
      {/* Export Status */}
      <div className="neon-panel p-6">
        <div className="neon-panel-header">
          Export System Status
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 font-mono text-sm">
          <div className="text-center">
            <div className="text-gray-400 mb-2">Data:</div>
            <div className={`font-mono text-lg ${hasResults ? 'text-neon-green' : 'text-red-400'}`}>
              {hasResults ? '[READY]' : '[NO DATA]'}
            </div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">CSV:</div>
            <div className="text-neon-blue font-mono text-lg">[AVAILABLE]</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Parquet:</div>
            <div className="text-neon-yellow font-mono text-lg">[BETA]</div>
          </div>
          <div className="text-center">
            <div className="text-gray-400 mb-2">Status:</div>
            <div className={`font-mono text-lg ${isButtonDisabled ? 'text-red-400' : 'text-neon-green'}`}>
              {isButtonDisabled ? '[DISABLED]' : '[ONLINE]'}
            </div>
          </div>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="neon-panel">
        <div className="neon-panel-header">
          Available Export Formats
        </div>
        <div className="p-8 space-y-8">
          {!hasResults && (
            <div className="neon-error mb-6 text-center max-w-2xl mx-auto">
              <strong>ERROR:</strong> No query results available. Execute a SQL query first.
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* CSV Export */}
            <div className="neon-panel p-6">
              <div className="text-center space-y-6">
                <div className="font-mono text-4xl text-neon-green">📄</div>
                <div className="space-y-4">
                  <h3 className="font-mono text-xl text-neon-green neon-glow-text">CSV FORMAT</h3>
                  <div className="font-mono text-sm space-y-2 text-gray-400">
                    <div>• Compatible with Excel</div>
                    <div>• Universal support</div>
                    <div>• Human readable</div>
                    <div>• Small file size</div>
                  </div>
                </div>
                <button
                  onClick={onExportCSV}
                  disabled={isButtonDisabled}
                  className={`neon-button neon-button-blue text-lg px-6 py-3 ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Export CSV
                </button>
              </div>
            </div>

            {/* Parquet Export */}
            <div className="neon-panel p-6">
              <div className="text-center space-y-6">
                <div className="font-mono text-4xl text-neon-yellow">🗃️</div>
                <div className="space-y-4">
                  <h3 className="font-mono text-xl text-neon-yellow neon-glow-text">PARQUET FORMAT</h3>
                  <div className="font-mono text-sm space-y-2 text-gray-400">
                    <div>• Columnar storage</div>
                    <div>• High compression</div>
                    <div>• Fast analytics</div>
                    <div>• [EXPERIMENTAL]</div>
                  </div>
                </div>
                <button
                  onClick={onExportParquet}
                  disabled={isButtonDisabled}
                  className={`neon-button neon-button-yellow text-lg px-6 py-3 ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Export Parquet
                </button>
              </div>
            </div>
          </div>

          {/* Format Comparison */}
          <div className="neon-panel p-6 max-w-4xl mx-auto">
            <div className="neon-panel-header">
              Format Comparison Matrix
            </div>
            <div className="overflow-auto">
              <table className="neon-table">
                <thead>
                  <tr>
                    <th>Feature</th>
                    <th>CSV</th>
                    <th>Parquet</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>File Size</td>
                    <td className="text-neon-yellow">Medium</td>
                    <td className="text-neon-green">Small</td>
                  </tr>
                  <tr>
                    <td>Compatibility</td>
                    <td className="text-neon-green">Universal</td>
                    <td className="text-neon-yellow">Limited</td>
                  </tr>
                  <tr>
                    <td>Performance</td>
                    <td className="text-neon-yellow">Standard</td>
                    <td className="text-neon-green">Fast</td>
                  </tr>
                  <tr>
                    <td>Human Readable</td>
                    <td className="text-neon-green">Yes</td>
                    <td className="text-red-400">No</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Footer Notes */}
          <div className="font-mono text-sm text-gray-500 space-y-2 text-center max-w-2xl mx-auto">
            <div>• Downloads will start automatically</div>
            <div>• File names include timestamp</div>
            <div>• All processing done locally in browser</div>
            <div>• No data uploaded to servers</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportButtons; 